// CandidatesList.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban, Bot, Briefcase, Building2, EyeOff, FileText, Globe, Link2, Mail,
  Newspaper, Pin, Trash2, Upload, X,
} from 'lucide-react';
import { candidatesApi, jobsApi } from '../../../shared/services/api';
import { useAuth } from '../../auth';
import { canWorkJob } from '../../../shared/utils/permissions';
import { candidateStatusBadge } from '../../../shared/utils/candidateStatus';

const SCORE_COLOR = (score) => {
  if (!score && score !== 0) return 'var(--text-muted)';
  if (score >= 75) return 'var(--green)';
  if (score >= 55) return 'var(--accent)';
  if (score >= 35) return 'var(--yellow)';
  return 'var(--red)';
};

const SOURCE_COLORS = {
  'LinkedIn': '#0077b5',
  'Indeed': '#2164f3',
  'Naukri': '#ff7555',
  'Monster': '#6e45e2',
  'Career Portal': '#3fb950',
  'Upload': '#8b949e',
  'Email': '#bc8cff',
};

const SOURCE_ICONS = {
  'LinkedIn': Link2,
  'Indeed': Briefcase,
  'Naukri': Newspaper,
  'Monster': Globe,
  'Career Portal': Building2,
  'Upload': Upload,
  'Email': Mail,
};

// Sources arrive as free-text, so anything unrecognised still needs a mark.
function SourceIcon({ source, size = 12 }) {
  const Icon = SOURCE_ICONS[source] || Pin;
  return <Icon size={size} strokeWidth={2} style={{ flexShrink: 0 }} />;
}

export default function CandidatesList() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState({ job_id: '', score_band: '' });
  const [showDeleted, setShowDeleted] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    Promise.all([candidatesApi.list(), jobsApi.list()])
      .then(([c, j]) => { setCandidates(c); setJobs(j); })
      .catch(() => { setCandidates([]); setJobs([]); })
      .finally(() => setLoading(false));
  }, []);

  const applyFilter = async (newFilter, deleted = showDeleted, rejected = showRejected) => {
    setLoading(true);
    const params = {};
    if (newFilter.job_id) params.job_id = newFilter.job_id;
    if (newFilter.score_band) params.score_band = newFilter.score_band;
    let fetchFn;
    if (deleted) fetchFn = candidatesApi.listDeleted;
    else if (rejected) fetchFn = candidatesApi.listRejected;
    else fetchFn = candidatesApi.list;
    fetchFn(params).then(setCandidates).catch(() => setCandidates([])).finally(() => setLoading(false));
  };

  const handleFilterChange = (key, val) => {
    const updated = { ...filter, [key]: val };
    setFilter(updated);
    applyFilter(updated);
  };

  const toggleShowDeleted = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    if (next) setShowRejected(false);
    applyFilter(filter, next, false);
  };

  const toggleShowRejected = () => {
    const next = !showRejected;
    setShowRejected(next);
    if (next) setShowDeleted(false);
    applyFilter(filter, false, next);
  };

  const clearFilters = () => {
    const cleared = { job_id: '', score_band: '' };
    setFilter(cleared);
    applyFilter(cleared);
  };

  const handleSoftDelete = async (candidateId, name) => {
    if (!window.confirm(`Soft-delete "${name}"? They will be hidden but can be restored.`)) return;
    setDeletingId(candidateId);
    try {
      await candidatesApi.softDelete(candidateId);
      applyFilter(filter);
    } catch (e) {
      alert('Delete failed: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (candidateId) => {
    setRestoringId(candidateId);
    try {
      await candidatesApi.restore(candidateId);
      applyFilter(filter);
    } catch (e) {
      alert('Restore failed: ' + e.message);
    } finally {
      setRestoringId(null);
    }
  };

  const hasActiveFilter = filter.job_id || filter.score_band;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Candidates</div>
          <div className="page-subtitle">
            {candidates.length} candidate{candidates.length !== 1 ? 's' : ''}{' '}
            {showDeleted ? '(showing deleted)' : showRejected ? '(showing rejected)' : hasActiveFilter ? '(filtered)' : 'across all jobs'}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          className="form-select" style={{ width: 220 }}
          value={filter.job_id}
          onChange={e => handleFilterChange('job_id', e.target.value)}
        >
          <option value="">All Jobs</option>
          {/* The code first: it is what people quote to each other, and two
              roles can carry near-identical titles. */}
          {jobs.filter(j => j.status !== 'Deleted').map(j => (
            <option key={j.id} value={j.id}>
              {j.job_code ? `${j.job_code} — ${j.title}` : j.title}
            </option>
          ))}
        </select>

        <select
          className="form-select" style={{ width: 180 }}
          value={filter.score_band}
          onChange={e => handleFilterChange('score_band', e.target.value)}
        >
          <option value="">All Score Bands</option>
          {['Strong Match', 'Good Match', 'Partial Match', 'Not Recommended'].map(b => (
            <option key={b}>{b}</option>
          ))}
        </select>

        {hasActiveFilter && (
          <button className="btn btn-ghost btn-sm" onClick={clearFilters}><X size={13} strokeWidth={2.2} /> Clear Filters</button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            onClick={toggleShowRejected}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--border)',
              background: showRejected ? 'rgba(239,68,68,0.1)' : 'var(--bg-surface)',
              color: showRejected ? 'var(--red)' : 'var(--text-muted)',
              transition: 'all 0.15s',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {showRejected
              ? <><EyeOff size={13} strokeWidth={2} /> Hide Rejected</>
              : <><Ban size={13} strokeWidth={2} /> Show Rejected</>}
          </button>
          <button
            onClick={toggleShowDeleted}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--border)',
              background: showDeleted ? 'rgba(239,68,68,0.1)' : 'var(--bg-surface)',
              color: showDeleted ? 'var(--red)' : 'var(--text-muted)',
              transition: 'all 0.15s',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {showDeleted
              ? <><EyeOff size={13} strokeWidth={2} /> Hide Deleted</>
              : <><Trash2 size={13} strokeWidth={2} /> Show Deleted</>}
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Source</th>
              <th>Current Role</th>
              <th>Exp</th>
              <th>Resume Score</th>
              <th>Interview</th>
              <th>Final</th>
              <th>Status</th>
              {showRejected && <th>Rejected At</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {candidates.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  {showRejected ? 'No rejected candidates found' : hasActiveFilter ? 'No candidates match the selected filters' : 'No candidates found'}
                </td>
              </tr>
            ) : candidates.map(c => (
              <tr key={c.id} style={{ opacity: (c.is_deleted || c.archived) ? 0.55 : 1, background: c.is_deleted ? 'rgba(239,68,68,0.03)' : undefined }}>
                <td>
                  <Link to={`/candidates/${c.id}`} style={{ color: c.is_deleted ? 'var(--text-muted)' : 'var(--accent)', fontWeight: 600, textDecoration: c.is_deleted ? 'line-through' : 'none' }}>
                    {c.name || 'Unknown'}
                  </Link>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div>
                </td>
                <td>
                  {c.source ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 10,
                      background: `${SOURCE_COLORS[c.source] || '#8b949e'}22`,
                      color: SOURCE_COLORS[c.source] || 'var(--text-muted)',
                      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>
                      <SourceIcon source={c.source} /> {c.source}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                  )}
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{c.current_role || '—'}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                  {c.total_experience_years ? `${c.total_experience_years}y` : '—'}
                </td>
                <td>
                  {c.resume_score !== null && c.resume_score !== undefined ? (
                    <div>
                      <span style={{ fontWeight: 700, color: SCORE_COLOR(c.resume_score), fontFamily: 'var(--font-mono)' }}>
                        {c.resume_score}/100
                      </span>
                      {c.resume_score_band && (
                        <div style={{ fontSize: 10, color: SCORE_COLOR(c.resume_score), marginTop: 1 }}>
                          {c.resume_score_band}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Pending</span>
                  )}
                </td>
                <td>
                  {(() => {
                    if (c.status === 'pending_second_round') {
                      return <span className="badge badge-yellow" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Pending</span>;
                    }
                    const raw = (c.interview_status || '').trim();
                    const label = raw === 'Completed' ? 'Completed'
                      : raw === 'Scheduled' ? 'Scheduled'
                      : raw === 'In Progress' ? 'In Progress'
                      : 'Not initiated';
                    const cls = raw === 'Completed' ? 'badge-green'
                      : raw === 'Scheduled' ? 'badge-yellow'
                      : raw === 'In Progress' ? 'badge-blue'
                      : 'badge-gray';
                    return <span className={`badge ${cls}`} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{label}</span>;
                  })()}
                </td>
                <td>
                  {c.final_score ? (
                    <span style={{ fontWeight: 700, color: SCORE_COLOR(c.final_score), fontFamily: 'var(--font-mono)' }}>
                      {c.final_score}
                    </span>
                  ) : '—'}
                </td>
                <td>
                  {(() => {
                    const s = candidateStatusBadge(c.status);
                    return <span className={`badge ${s.cls}`} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{s.label}</span>;
                  })()}
                </td>
                {showRejected && (
                  <td style={{ fontSize: 11, color: 'var(--red)' }}>
                    {c.pipeline_stage === 'rejected_resume'
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FileText size={12} strokeWidth={2} /> Resume stage</span>
                      : c.pipeline_stage === 'rejected_interview'
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Bot size={12} strokeWidth={2} /> Interview stage</span>
                      : '—'}
                    {c.rejection_email_sent && (
                      <div style={{ color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} strokeWidth={2} /> Email sent</div>
                    )}
                  </td>
                )}
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Link to={`/candidates/${c.id}`} className="btn btn-ghost btn-sm">View</Link>
                    {!canWorkJob(user, jobs.find(j => j.id === c.job_id)) ? null : c.is_deleted ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--green)', fontSize: 11 }}
                        onClick={() => handleRestore(c.id)}
                        disabled={restoringId === c.id}
                      >
                        {restoringId === c.id ? '...' : 'Restore'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--red)', fontSize: 11 }}
                        onClick={() => handleSoftDelete(c.id, c.name || 'Unknown')}
                        disabled={deletingId === c.id}
                      >
                        {deletingId === c.id ? '...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
